import { Button } from '../ui/button';
import { ArrowRight, BarChart3, PieChart, TrendingUp, Database } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import zohoAnalyticsLogo from "/assets/zoho-products/zoho-analytics.png";

export function ZohoAnalytics() {
  const benefits = [
    {
      icon: <BarChart3 className="h-8 w-8 text-purple-600" />,
      title: "Real-time Dashboards",
      description: "Monitor key metrics with interactive, real-time dashboards"
    },
    {
      icon: <PieChart className="h-8 w-8 text-purple-600" />,
      title: "Custom Reports",
      description: "Create detailed reports tailored to your business needs"
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-purple-600" />,
      title: "Predictive Analytics",
      description: "Forecast trends and make data-driven decisions"
    },
    {
      icon: <Database className="h-8 w-8 text-purple-600" />,
      title: "Data Integration",
      description: "Connect multiple data sources for comprehensive insights"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image First */}
          <div className="relative order-2 lg:order-1">
            <ImageWithFallback 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=500&fit=crop"
              alt="Zoho Analytics Dashboard"
              className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
            />
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-4 rotate-3">
              <img 
                src={zohoAnalyticsLogo}
                alt="Zoho Analytics" 
                className="h-8 w-8 object-contain"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 -rotate-3">
              <div className="text-xl font-bold text-purple-600">95%</div>
              <div className="text-xs text-gray-600">Accuracy</div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8 order-1 lg:order-2">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full mb-4">
                <img 
                  src={zohoAnalyticsLogo}
                  alt="Zoho Analytics" 
                  className="h-4 w-4 mr-2 object-contain"
                />
                Zoho Analytics
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Turn Data into Actionable Insights
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Unlock the power of your business data with Zoho Analytics. Create stunning 
                visualizations, generate automated reports, and make informed decisions.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4">Key Features:</h3>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <ArrowRight className="h-4 w-4 text-purple-600 mr-2" />
                  Drag & Drop Interface
                </div>
                <div className="flex items-center">
                  <ArrowRight className="h-4 w-4 text-purple-600 mr-2" />
                  AI-Powered Insights
                </div>
                <div className="flex items-center">
                  <ArrowRight className="h-4 w-4 text-purple-600 mr-2" />
                  Mobile Dashboards
                </div>
                <div className="flex items-center">
                  <ArrowRight className="h-4 w-4 text-purple-600 mr-2" />
                  Scheduled Reports
                </div>
              </div>
            </div>

            <Button className="bg-purple-600 hover:bg-purple-700 text-white">
              Explore Analytics Solutions
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}